import BaseService from "../BaseService";
import { GoogleUser, User, UserOrderBy } from "../../stores/AuthStore/types";

export interface LoginPayload {
  username: string;
  password: string;
  captchaId: string;
  captchaCode: string;
}

export interface LoginResponse {
  userId: string;
  token: string;
}

export interface ForgotPasswordPayload {
  username: string;
  captchaId: string;
  captchaCode: string;
}

export interface VerifyEmailPayload {
  email: string;
  phone: string;
  password: string;
  captchaId: string;
  captchaCode: string;
}

class AuthService extends BaseService {
  userLogin(
    username: string,
    password: string,
    captchaId: string,
    captchaCode: string
  ): Promise<LoginResponse> {
    return this.post<LoginResponse, LoginPayload>(`login`, {
      username,
      password,
      captchaId,
      captchaCode,
    });
  }

  googleLogin(access_token: string): Promise<LoginResponse> {
    return this.post<LoginResponse, { access_token: string }>(`googleLogin`, {
      access_token,
    });
  }

  googleRegister(access_token: string): Promise<LoginResponse> {
    return this.post<LoginResponse, { access_token: string }>(
      `googleRegister`,
      {
        access_token,
      }
    );
  }

  appleLogin(access_token: string): Promise<LoginResponse> {
    return this.post<LoginResponse, { access_token: string }>(`appleLogin`, {
      access_token,
    });
  }

  appleRegister(
    access_token: string,
    given_name: string,
    family_name: string
  ): Promise<LoginResponse> {
    return this.post<
      LoginResponse,
      { access_token: string; given_name: string; family_name: string }
    >(`appleRegister`, {
      access_token,
      given_name,
      family_name,
    });
  }

  userLogout(): Promise<void> {
    return this.post(`logout`);
  }

  fetchCaptcha(): Promise<{ captchaId: string; image: string }> {
    return this.post<{ captchaId: string; image: string }, void>(
      `captcha/generate`
    );
  }

  verifyCaptcha(captchaId: string, userInput: string): Promise<boolean> {
    return this.post<boolean, { captchaId: string; userInput: string }>(
      `captcha/verify`,
      { captchaId, userInput }
    );
  }

  fetchGoogleUser(accessToken: string): Promise<GoogleUser> {
    return this.fetch<GoogleUser>(
      `https://www.googleapis.com/oauth2/v1/userinfo?alt=json&access_token=${accessToken}`
    );
  }

  fetchUser(): Promise<User> {
    return this.fetch<User>(`user`).then(
      (user): User => ({ ...user, id: (user as any)?._id })
    );
  }

  fetchPendingRegistrations(): Promise<User[]> {
    return this.fetch<User[]>("pendingLawyers").then((result) =>
      result.map((user) => ({ ...user, id: (user as any)._id }))
    );
  }

  updateUser(user: User): Promise<User> {
    return this.put<User, User>(`users/${user.id}`, user);
  }

  approveLawyer(lawyer: User): Promise<void> {
    return this.post(`approveLawyer/${lawyer.id}`);
  }

  rejectLawyer(lawyer: User): Promise<void> {
    return this.post(`rejectLawyer/${lawyer.id}`, {
      reason: lawyer.reason,
    });
  }

  forgotPassword(
    email: string,
    captchaId: string,
    captchaCode: string
  ): Promise<void> {
    return this.post<any, ForgotPasswordPayload>("forgotPassword", {
      username: email,
      captchaId,
      captchaCode,
    });
  }

  registerUser(
    email: string,
    password: string,
    phone: string,
    captchaId: string,
    captchaCode: string
  ) {
    return this.post<any, VerifyEmailPayload>("register", {
      email,
      password,
      phone,
      captchaId,
      captchaCode,
    });
  }

  searchUsers(
    keyword: string,
    orderBy: UserOrderBy,
    page: number,
    size: number
  ): Promise<{ users: User[]; count: number }> {
    return this.fetch<{ users: User[]; count: any }[]>("searchUsers", {
      keyword,
      orderBy,
      page: page.toString(),
      size: size.toString(),
    }).then((result) => ({
      ...result[0],
      users: result[0]?.users?.map(
        (userItem): User => ({ ...userItem, id: (userItem as any)._id })
      ),
      count: result[0]?.count[0]?.count || 0,
    }));
  }

  sendEmailCode(params: { newEmail: string }): Promise<void> {
    return this.post("sendEmailCode", params);
  }

  validateEmailCode(code: string, email?: string): Promise<boolean> {
    return this.post("validateEmailCode", { code, email });
  }

  /*
  sendPhoneCode(params: { newPhone: string }): Promise<void> {
    return this.post("sendPhoneCode", params);
  }

  validatePhoneCode(code: string): Promise<boolean> {
    return this.post("validatePhoneCode", { code });
  }
  */

  validateHash(hash: string): Promise<void> {
    return this.post("validateHash", {
      hash,
    });
  }

  resetPassword(hash: string, password: string): Promise<void> {
    return this.post("resetPassword", {
      hash,
      password,
    });
  }

  changePassword(password: string): Promise<void> {
    return this.post("changePassword", {
      password,
    });
  }

  deactivateUser(): Promise<void> {
    return this.post("deactivateUser");
  }
}

export default AuthService;
